'use client'


import Link from "next/link"
import Image from "next/image"
import { useState, useEffect } from "react"
import { Button } from "../ui/button"
import { motion, AnimatePresence } from "framer-motion"
import { getImageUrl } from "@/shared/utils/image"

const images: { src: string }[] = [
    { src: "/studio-collection/Copy of IJT_0608.JPG" },
    { src: "/studio-collection/KDTX0002.jpg" },
    { src: "/talent-collection-1/KDTX0080.jpg" },
    { src: "/talent-collection-2/KDTX0244.jpg" },
    { src: "/talent-collection-3/KDTX0129.jpg" },
]

export const HeroSection = () => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const activeImage = images[currentIndex]?.src ?? images[0]?.src ?? null

    useEffect(() => {
        if (images.length <= 1) {
            return undefined
        }

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length)
        }, 6000)

        return () => clearInterval(interval)
    }, [])

    return (
        <section className="relative min-h-screen w-full overflow-hidden bg-stone-950">
            {activeImage ? (
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentIndex}
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                        className="absolute inset-0"
                    >
                        <Image
                            src={getImageUrl(activeImage)}
                            alt={`Hero image ${currentIndex + 1}`}
                            fill
                            priority={currentIndex === 0}
                            className="object-cover object-center brightness-[0.72]"
                            sizes="100vw"
                        />
                    </motion.div>
                </AnimatePresence>
            ) : null}

            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.14),transparent_30%),linear-gradient(120deg,rgba(17,24,39,0.88),rgba(28,25,23,0.56)_42%,rgba(17,24,39,0.86)_100%)]" />
            <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-stone-950 via-stone-950/70 to-transparent" />

            <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl items-center px-6 pt-28 pb-12 lg:px-12 lg:pt-36 lg:pb-16">
                <div className="max-w-4xl">
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.25 }}
                        className="text-[11px] uppercase tracking-[0.42em] text-stone-200/80"
                    >
                        Raya Photography
                    </motion.p>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.85, delay: 0.4 }}
                        className="mt-6 font-serif text-5xl leading-[1.02] tracking-[-0.04em] text-white sm:text-6xl lg:text-8xl"
                    >
                        Celebrate Raya
                        <span className="block text-stone-200">with warm, timeless portraits.</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.75, delay: 0.55 }}
                        className="mt-5 max-w-3xl text-sm uppercase tracking-[0.3em] text-stone-200/78 sm:text-base"
                    >
                        Family | Couples | Portraits
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.75, delay: 0.7 }}
                        className="mt-6 max-w-xl text-base leading-8 text-stone-200/78 sm:text-lg"
                    >
                        Professional studio photography for festive family moments and polished seasonal portraits.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.75, delay: 0.85 }}
                        className="mt-8"
                    >
                        <Button
                            asChild
                            size="lg"
                            className="h-14 rounded-full bg-white px-8 text-xs uppercase tracking-[0.28em] text-stone-950 hover:bg-stone-100"
                        >
                            <Link href="/raya/booking">
                                Book Now
                            </Link>
                        </Button>
                    </motion.div>
                </div>
            </div>

            {images.length > 1 ? (
                <div className="absolute bottom-12 right-12 flex gap-3">
                    {images.map((_img, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => setCurrentIndex(idx)}
                            className="group relative h-1 w-12 overflow-hidden rounded-full bg-white/20 transition-all"
                            aria-label={`Show hero image ${idx + 1}`}
                        >
                            <motion.div
                                className="absolute inset-0 bg-white"
                                initial={false}
                                animate={{
                                    x: idx === currentIndex ? "0%" : "-100%",
                                }}
                                transition={{ duration: 0.5 }}
                            />
                        </button>
                    ))}
                </div>
            ) : null}
        </section>
    )
}
